import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
    Box,
    Paper,
    Typography,
    Button, 
    Alert,
    CircularProgress,
    Container
} from '@mui/material';
import { CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import { resetPassword } from '../api';
import { PasswordInput } from '../components/Common.jsx';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator.jsx';
import { validatePassword as checkStrength } from '../utils/passwordValidation';

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: null, message: '' });
    const [passwordPolicy, setPasswordPolicy] = useState(null);

    React.useEffect(() => {
        const fetchPolicy = async () => {
            if (!token) return;
            try {
                // Public endpoint — must NOT send any auth token (avoids 401 from stale tokens)
                const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/+$/, '') || '';
                const res = await fetch(`${API_BASE}/auth/password-policy?token=${encodeURIComponent(token)}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (res.ok) {
                    const policy = await res.json();
                    setPasswordPolicy(policy);
                } else {
                    console.warn('Could not load password policy, using defaults.');
                }
            } catch (err) {
                console.error('Failed to fetch password policy:', err);
            }
        };
        fetchPolicy();
    }, [token]);

    const validation = React.useMemo(() => checkStrength(password, passwordPolicy), [password, passwordPolicy]);

    const handleReset = async (e) => {
        e.preventDefault();
        
        if (!validation.isValid) {
            setStatus({ type: 'error', message: "Password does not meet the security requirements." });
            return;
        }


        if (password !== confirmPassword) {
            setStatus({ type: 'error', message: "Passwords do not match." });
            return;
        }

        if (!token) {
            setStatus({ type: 'error', message: "Invalid or missing reset token." });
            return;
        }

        setLoading(true);
        setStatus({ type: null, message: '' });

        try {
            const res = await resetPassword(token, password);
            setStatus({ 
                type: 'success', 
                message: res.message || "Password updated successfully! Redirecting to login..." 
            });
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setStatus({ 
                type: 'error', 
                message: err.message || "Failed to reset password. The link may have expired." 
            });
        } finally {
            setLoading(false);
        }
    };

    if (!token && status.type !== 'success') {
        return (
            <Box className="h-screen flex items-center justify-center bg-gray-50 p-6">
                <Paper elevation={0} className="max-w-md w-full p-8 text-center rounded-2xl border shadow-xl">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <Typography variant="h5" className="font-bold mb-2">Invalid Reset Link</Typography>
                    <Typography className="text-gray-500 mb-6">
                        This password reset link is invalid or missing a token.
                    </Typography>
                    <Button 
                        variant="contained" 
                        fullWidth 
                        onClick={() => navigate('/login')}
                        className="bg-primary hover:bg-primary/90 text-white py-3 rounded-xl font-bold capitalize"
                    >
                        Back to Login
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box className="min-h-screen flex items-center justify-center bg-gray-50 p-6 font-sans">
            <Container maxWidth="sm">
                <Paper 
                    elevation={0} 
                    className="p-10 rounded-3xl border shadow-2xl relative overflow-hidden"
                >
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/5 rounded-full -ml-12 -mb-12" />

                    <div className="relative z-10">
                        <Box className="mb-8">
                            <Typography variant="h4" className="font-black text-gray-900 mb-2 tracking-tight">
                                Reset Password
                            </Typography>
                            <Typography className="text-gray-500 font-medium">
                                Create a secure new password for your AIDAS account.
                            </Typography>
                        </Box>

                        {status.message && (
                            <Alert 
                                severity={status.type} 
                                className={`mb-8 rounded-xl border ${status.type === 'success' ? 'border-green-100 bg-green-50' : 'border-red-100 bg-red-50'}`}
                                icon={status.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                            >
                                {status.message}
                            </Alert>
                        )}

                        {status.type !== 'success' && (
                            <form onSubmit={handleReset} className="space-y-6">
                                <PasswordInput
                                    label="New Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    placeholder="Enter new password"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '14px',
                                            backgroundColor: 'rgba(0,0,0,0.02)',
                                        },
                                        '& .dark .MuiOutlinedInput-root': {
                                            backgroundColor: 'rgba(255,255,255,0.02)',
                                        }
                                    }}
                                />
                                <PasswordStrengthIndicator 
                                    password={password} 
                                    policy={passwordPolicy}
                                />

                                <PasswordInput
                                    label="Confirm New Password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    placeholder="Re-enter new password"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '14px',
                                            backgroundColor: 'rgba(0,0,0,0.02)',
                                        }
                                    }}
                                />

                                <Button
                                    type="submit"
                                    fullWidth
                                    disabled={loading}
                                    variant="contained"
                                    className="bg-primary hover:bg-primary/90 text-white transition-all transform hover:scale-[1.02] active:scale-[0.98] py-4 rounded-xl font-bold text-lg shadow-xl shadow-primary/20 capitalize"
                                >

                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
                                </Button>

                                <Button 
                                    fullWidth 
                                    onClick={() => navigate('/login')}
                                    className="text-gray-500 font-bold hover:bg-gray-100 py-3 rounded-xl transition-colors gap-2 capitalize"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Back to Login
                                </Button>
                            </form>
                        )}
                    </div>
                </Paper>
            </Container>
        </Box>
    );
};

export default ResetPasswordPage;
